import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { ChevronLeft, ChevronRight, CheckCircle2, User, Building2, Lightbulb, FileCheck } from 'lucide-react';

interface OnboardingData {
  companyName: string;
  description: string;
  website: string;
  contactPersonName: string;
  contactEmail: string;
  phone: string;
  addressStreet: string;
  addressCity: string;
  addressPostalCode: string;
  addressCountry: string;
  employeeCount: number;
  industry: string;
  revenueRange: string;
  businessBranches: string;
  aiPainPoints: string;
  aiAdoptionStage: string;
}

const EMPTY_FORM_DATA: OnboardingData = {
  companyName: '',
  description: '',
  website: '',
  contactPersonName: '',
  contactEmail: '',
  phone: '',
  addressStreet: '',
  addressCity: '',
  addressPostalCode: '',
  addressCountry: '',
  employeeCount: 0,
  industry: '',
  revenueRange: '',
  businessBranches: '',
  aiPainPoints: '',
  aiAdoptionStage: '',
};

// Step icons for visual hierarchy
const STEP_ICONS = [
  { icon: CheckCircle2, color: 'text-primary' },
  { icon: User, color: 'text-blue-500 dark:text-orange-400' },
  { icon: Building2, color: 'text-blue-500 dark:text-orange-400' },
  { icon: Lightbulb, color: 'text-blue-500 dark:text-orange-400' },
  { icon: FileCheck, color: 'text-blue-500 dark:text-orange-400' },
];

// Fade-in animation wrapper
function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  return (
    <div
      className="animate-in fade-in duration-500"
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function CompanyOnboarding() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<OnboardingData>(EMPTY_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const totalSteps = 5;

  const updateFormData = (field: keyof OnboardingData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 2:
        if (!formData.contactPersonName || formData.contactPersonName.length < 2) {
          toast({
            variant: 'destructive',
            title: 'Validation Error',
            description: 'Contact person name must be at least 2 characters.',
          });
          return false;
        }
        if (!formData.contactEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
          toast({
            variant: 'destructive',
            title: 'Validation Error',
            description: 'Please enter a valid email address.',
          });
          return false;
        }
        return true;
      case 3:
        if (!formData.employeeCount || formData.employeeCount < 1) {
          toast({
            variant: 'destructive',
            title: 'Validation Error',
            description: 'Employee count must be at least 1.',
          });
          return false;
        }
        return true;
      case 4:
        if (!formData.aiPainPoints || formData.aiPainPoints.length < 20) {
          toast({
            variant: 'destructive',
            title: 'Validation Error',
            description: 'Please describe your AI challenges (minimum 20 characters).',
          });
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/company/onboarding/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to complete onboarding');
      }

      toast({
        title: 'Welcome to securesight.ai!',
        description: 'Your company profile has been set up successfully.',
      });

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Onboarding submission error:', error);
      toast({
        variant: 'destructive',
        title: 'Submission Failed',
        description: error instanceof Error ? error.message : 'An error occurred while submitting your profile.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    const StepIcon = STEP_ICONS[currentStep - 1]?.icon || CheckCircle2;
    const stepIconColor = STEP_ICONS[currentStep - 1]?.color || 'text-primary';

    switch (currentStep) {
      case 1:
        return (
          <FadeIn>
            <div className="space-y-6">
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl" />
                    <StepIcon className={`relative h-20 w-20 ${stepIconColor}`} />
                  </div>
                </div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Welcome to securesight.ai!
                </h2>
                <p className="text-muted-foreground text-lg">
                  We're excited to have you on board. Before you can access all features, we need to gather some essential information about your company.
                </p>
                <p className="text-sm text-muted-foreground">
                  This will only take a few minutes and helps us tailor our services to your needs.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-6 space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  What we'll ask you:
                </h3>
                <ul className="space-y-3 text-sm">
                  {[
                    { icon: User, text: 'Contact information for your company' },
                    { icon: Building2, text: 'Basic business details (industry, company size, revenue)' },
                    { icon: Lightbulb, text: 'Your AI challenges and adoption stage' },
                    { icon: FileCheck, text: 'Review and confirm your information' },
                  ].map((item, idx) => {
                    const ItemIcon = item.icon;
                    return (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary font-bold text-xs shrink-0">
                          {idx + 1}
                        </span>
                        <span className="text-muted-foreground flex items-center gap-2">
                          <ItemIcon className="h-4 w-4 text-primary" />
                          {item.text}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="bg-blue-500/10 dark:bg-orange-500/10 border border-blue-500/20 dark:border-orange-500/20 rounded-lg p-4">
                <p className="text-sm text-blue-700 dark:text-orange-400">
                  <strong>Note:</strong> Required fields are marked with an asterisk (<span className="text-red-500">*</span>). You can always update this information later in your company settings.
                </p>
              </div>
            </div>
          </FadeIn>
        );

      case 2:
        return (
          <FadeIn>
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <StepIcon className={`h-6 w-6 ${stepIconColor}`} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Contact Information</h2>
                  <p className="text-muted-foreground">Let us know who to contact regarding your securesight.ai account.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) => updateFormData('companyName', e.target.value)}
                    placeholder="Your company name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPersonName" className="font-semibold">
                    Contact Person Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contactPersonName"
                    value={formData.contactPersonName}
                    onChange={(e) => updateFormData('contactPersonName', e.target.value)}
                    placeholder="Jane Doe"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Primary person we should contact</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactEmail" className="font-semibold">
                    Contact Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => updateFormData('contactEmail', e.target.value)}
                    placeholder="contact@company.com"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Email for company communications</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    Phone Number <span className="text-muted-foreground font-normal">(Optional)</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateFormData('phone', e.target.value)}
                    placeholder="+1-555-0123"
                  />
                  <p className="text-xs text-muted-foreground">Optional: For urgent matters</p>
                </div>

                <div className="space-y-4">
                  <Label className="font-semibold">
                    Business Address <span className="text-muted-foreground font-normal">(Optional)</span>
                  </Label>
                  <div className="space-y-3 pl-4 border-l-2 border-muted-foreground/20">
                    <div>
                      <Input
                        placeholder="Street Address"
                        value={formData.addressStreet}
                        onChange={(e) => updateFormData('addressStreet', e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="City"
                        value={formData.addressCity}
                        onChange={(e) => updateFormData('addressCity', e.target.value)}
                      />
                      <Input
                        placeholder="Postal Code"
                        value={formData.addressPostalCode}
                        onChange={(e) => updateFormData('addressPostalCode', e.target.value)}
                      />
                    </div>
                    <div>
                      <Input
                        placeholder="Country"
                        value={formData.addressCountry}
                        onChange={(e) => updateFormData('addressCountry', e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">For billing and official correspondence</p>
                </div>
              </div>
            </div>
          </FadeIn>
        );

      case 3:
        return (
          <FadeIn>
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <StepIcon className={`h-6 w-6 ${stepIconColor}`} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Business Details</h2>
                  <p className="text-muted-foreground">Help us understand your business better.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="employeeCount" className="font-semibold">
                    Number of Employees <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="employeeCount"
                    type="number"
                    min="1"
                    value={formData.employeeCount || ''}
                    onChange={(e) => updateFormData('employeeCount', parseInt(e.target.value) || 0)}
                    placeholder="50"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Total number of employees in your company</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    value={formData.industry}
                    onChange={(e) => updateFormData('industry', e.target.value)}
                    placeholder="Technology, Healthcare, Finance, etc."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="revenueRange">Annual Revenue Range</Label>
                  <Select value={formData.revenueRange} onValueChange={(value) => updateFormData('revenueRange', value)}>
                    <SelectTrigger id="revenueRange">
                      <SelectValue placeholder="Select revenue range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-1M">Under $1M</SelectItem>
                      <SelectItem value="1M-5M">$1M - $5M</SelectItem>
                      <SelectItem value="5M-10M">$5M - $10M</SelectItem>
                      <SelectItem value="10M-50M">$10M - $50M</SelectItem>
                      <SelectItem value="50M-100M">$50M - $100M</SelectItem>
                      <SelectItem value="100M+">$100M+</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Helps us tailor our offering to your needs</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessBranches">Business Areas / Branches</Label>
                  <Textarea
                    id="businessBranches"
                    value={formData.businessBranches}
                    onChange={(e) => updateFormData('businessBranches', e.target.value)}
                    placeholder="AI Consulting&#10;Machine Learning Solutions&#10;Data Analytics"
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">Enter each business area on a new line</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Company Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => updateFormData('website', e.target.value)}
                    placeholder="https://www.company.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Company Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => updateFormData('description', e.target.value)}
                    placeholder="Brief description of your company..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </FadeIn>
        );

      case 4:
        return (
          <FadeIn>
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <StepIcon className={`h-6 w-6 ${stepIconColor}`} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">AI Needs & Challenges</h2>
                  <p className="text-muted-foreground">Tell us about your AI journey and where you need help.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="aiPainPoints" className="font-semibold">
                    AI Challenges <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="aiPainPoints"
                    value={formData.aiPainPoints}
                    onChange={(e) => updateFormData('aiPainPoints', e.target.value)}
                    placeholder="We struggle with automating customer support, analyzing large datasets efficiently, and integrating AI into our existing workflows..."
                    rows={5}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Describe specific challenges where AI could help your business (minimum 20 characters)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="aiAdoptionStage">Current AI Adoption Stage</Label>
                  <Select value={formData.aiAdoptionStage} onValueChange={(value) => updateFormData('aiAdoptionStage', value)}>
                    <SelectTrigger id="aiAdoptionStage">
                      <SelectValue placeholder="Select your stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exploring">Exploring - Just starting to learn about AI</SelectItem>
                      <SelectItem value="piloting">Piloting - Running small AI experiments</SelectItem>
                      <SelectItem value="implementing">Implementing - Deploying AI in production</SelectItem>
                      <SelectItem value="scaling">Scaling - Expanding AI across the organization</SelectItem>
                      <SelectItem value="mature">Mature - AI is integral to our operations</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">How far along is your AI journey?</p>
                </div>
              </div>
            </div>
          </FadeIn>
        );

      case 5:
        return (
          <FadeIn>
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <StepIcon className={`h-6 w-6 ${stepIconColor}`} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Review Your Information</h2>
                  <p className="text-muted-foreground">Please review all information before submitting.</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Contact Info Section */}
                <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Contact Information
                  </h3>
                  <div className="pl-7 space-y-2 text-sm">
                    <div><span className="font-medium">Company Name:</span> {formData.companyName || <span className="text-muted-foreground">Not provided</span>}</div>
                    <div><span className="font-medium">Contact Person:</span> {formData.contactPersonName}</div>
                    <div><span className="font-medium">Contact Email:</span> {formData.contactEmail}</div>
                    {formData.phone && <div><span className="font-medium">Phone:</span> {formData.phone}</div>}
                    {(formData.addressStreet || formData.addressCity) && (
                      <div><span className="font-medium">Address:</span> {[
                        formData.addressStreet,
                        formData.addressCity,
                        formData.addressPostalCode,
                        formData.addressCountry
                      ].filter(Boolean).join(', ')}</div>
                    )}
                  </div>
                </div>

                {/* Business Details Section */}
                <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    Business Details
                  </h3>
                  <div className="pl-7 space-y-2 text-sm">
                    <div><span className="font-medium">Employee Count:</span> {formData.employeeCount}</div>
                    {formData.industry && <div><span className="font-medium">Industry:</span> {formData.industry}</div>}
                    {formData.revenueRange && <div><span className="font-medium">Revenue Range:</span> {formData.revenueRange}</div>}
                    {formData.businessBranches && (
                      <div><span className="font-medium">Business Areas:</span>
                        <ul className="mt-1 ml-4 list-disc space-y-1">
                          {formData.businessBranches.split('\n').filter(Boolean).map((branch, idx) => (
                            <li key={idx}>{branch}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {formData.website && <div><span className="font-medium">Website:</span> {formData.website}</div>}
                    {formData.description && <div><span className="font-medium">Description:</span> {formData.description}</div>}
                  </div>
                </div>

                {/* AI Needs Section */}
                <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-primary" />
                    AI Needs & Challenges
                  </h3>
                  <div className="pl-7 space-y-2 text-sm">
                    <div><span className="font-medium">AI Challenges:</span> {formData.aiPainPoints}</div>
                    {formData.aiAdoptionStage && <div><span className="font-medium">AI Adoption Stage:</span> {formData.aiAdoptionStage}</div>}
                  </div>
                </div>

                <div className="bg-blue-500/10 dark:bg-orange-500/10 border border-blue-500/20 dark:border-orange-500/20 rounded-lg p-4">
                  <p className="text-sm text-blue-700 dark:text-orange-400">
                    <strong>Important:</strong> You can always update this information later in your company settings.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        );

      default:
        return null;
    }
  };

  const progressPercentage = ((currentStep - 1) / (totalSteps - 1)) * 100;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl shadow-lg">
        <CardHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl">Company Onboarding</CardTitle>
              <div className="text-sm text-muted-foreground font-medium">
                Step {currentStep} of {totalSteps}
              </div>
            </div>

            {/* Enhanced Progress Bar with Step Indicators */}
            <div className="space-y-3">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500 ease-in-out"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="flex justify-between">
                {STEP_ICONS.map((step, idx) => {
                  const StepIcon = step.icon;
                  const stepNumber = idx + 1;
                  const isCompleted = stepNumber < currentStep;
                  const isCurrent = stepNumber === currentStep;

                  return (
                    <div
                      key={idx}
                      className={`flex flex-col items-center gap-1 ${
                        isCurrent ? 'scale-110' : ''
                      } transition-transform duration-200`}
                    >
                      <div
                        className={`
                          flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-300
                          ${isCompleted ? 'bg-primary border-primary text-primary-foreground' : ''}
                          ${isCurrent ? `border-primary ${step.color} bg-primary/10` : 'border-muted bg-background text-muted-foreground'}
                        `}
                      >
                        <StepIcon className={`h-4 w-4 ${isCompleted ? '' : step.color}`} />
                      </div>
                      <span
                        className={`
                          text-[10px] font-medium hidden sm:block transition-colors duration-300
                          ${isCurrent ? 'text-foreground' : 'text-muted-foreground'}
                        `}
                      >
                        {idx === 0 && 'Welcome'}
                        {idx === 1 && 'Contact'}
                        {idx === 2 && 'Business'}
                        {idx === 3 && 'AI'}
                        {idx === 4 && 'Review'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="min-h-[400px]">
            {renderStep()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1 || isLoading}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            {currentStep < totalSteps ? (
              <Button onClick={handleNext} disabled={isLoading} className="gap-2">
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isLoading} className="gap-2">
                {isLoading ? 'Submitting...' : 'Complete Onboarding'}
                <CheckCircle2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
